// compassionDaily.ts — the daily cadence for the self-compassion course.
//
// The course is a sequence of intro lessons (founder's voice, readings — done
// freely up front) followed by day-gated exercises and meditation days. Only ONE
// day-exercise unlocks per calendar day: today's is the next incomplete day-step,
// and it's locked once any day-step has been completed today ("come back
// tomorrow"). Intros are never day-gated. Dev mode bypasses the gate.
import type { Programme, Step } from '@/types';
import type { ProgressState } from './progress';
import { allSteps } from './contentProvider';
import { dayKey } from './dates';

export const isDayStep = (s: Step): boolean => !!s.day_label;

/** How many distinct days a step asks to be practised, read from its day label.
 *  A multi-day label like "Day 4, 5 &6" returns 3; a single "Day 3" returns 1.
 *  Honouring the labels lets a step gently hold the user for several days before
 *  the next one unlocks (Batch B #8). */
export function daysRequired(step: Step): number {
  if (!step.day_label) return 1;
  const nums = step.day_label.match(/\d+/g);
  return Math.max(1, nums ? nums.length : 1);
}

/** Distinct calendar days the user has practised a held (multi-day) step. */
export function practiceDaysDone(progress: ProgressState, stepId: string): number {
  return (progress.practiceDays?.[stepId] ?? []).length;
}

/** The week number shown to the user (1–6), parsed from the module title; 0 = intro. */
export function weekOf(programme: Programme, step: Step): number {
  const mod = programme.modules.find((m) => m.steps.some((s) => s.id === step.id));
  const m = mod && /week\s*(\d+)/i.exec(`${mod.title.pre} ${mod.title.em}`);
  return m ? Number(m[1]) : 0;
}

export function dayTotals(programme: Programme, progress: ProgressState): { total: number; done: number; left: number } {
  const days = allSteps(programme).filter(isDayStep);
  const done = days.filter((s) => progress.completedSteps.includes(s.id)).length;
  return { total: days.length, done, left: Math.max(0, days.length - done) };
}

function sameDay(iso: string | undefined, ref: Date): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth() && d.getDate() === ref.getDate();
}

/** Has the user already done their one day-exercise today? (the daily limit). This
 *  also counts a multi-day held step PRACTISED today (even though it isn't finished
 *  yet), so a held step still rests the user until tomorrow. */
export function didDayToday(programme: Programme, progress: ProgressState): boolean {
  const today = new Date();
  const todayKey = dayKey(today);
  return allSteps(programme)
    .filter(isDayStep)
    .some((s) => sameDay(progress.completedAt[s.id], today) || (progress.practiceDays?.[s.id] ?? []).includes(todayKey));
}

/** The day-exercise the user completed today (most recent) — so it can be reopened. */
export function dayDoneToday(programme: Programme, progress: ProgressState): Step | null {
  const today = new Date();
  const done = allSteps(programme).filter(isDayStep).filter((s) => sameDay(progress.completedAt[s.id], today));
  if (!done.length) {
    // A multi-day held step practised today isn't complete but is today's practice: reopenable.
    const held = nextStep(programme, progress);
    return held && isDayStep(held) && (progress.practiceDays?.[held.id] ?? []).includes(dayKey(today)) ? held : null;
  }
  done.sort((a, b) => ((progress.completedAt[a.id] ?? '') < (progress.completedAt[b.id] ?? '') ? 1 : -1));
  return done[0];
}

/** The next not-yet-completed step in course order (intro or day). */
export function nextStep(programme: Programme, progress: ProgressState): Step | null {
  return allSteps(programme).find((s) => !progress.completedSteps.includes(s.id)) ?? null;
}

export interface DailyState {
  step: Step | null;       // the step to surface (today's exercise or the next intro)
  isDay: boolean;          // is it a day-gated exercise/meditation?
  locked: boolean;         // a day-exercise is already done today → wait until tomorrow
  weekNo: number;          // 1–6, or 0 for the intro chapter
  dayLabel: string | null; // e.g. "Day 2"
  left: number;            // day-exercises remaining in the course
  total: number;           // total day-exercises
  finished: boolean;       // nothing left
  doneToday: Step | null;  // today's completed exercise (reopenable while locked)
  /** For a multi-day held step: how many of its days are done, and how many it asks
   *  for. null when the current step is a normal one-day step. */
  held: { done: number; need: number } | null;
}

export function dailyState(programme: Programme, progress: ProgressState, devMode: boolean): DailyState {
  const step = nextStep(programme, progress);
  const { total, left } = dayTotals(programme, progress);
  const isDay = !!step && isDayStep(step);
  const locked = isDay && !devMode && didDayToday(programme, progress);
  const need = step ? daysRequired(step) : 1;
  const held = step && need > 1 ? { done: practiceDaysDone(progress, step.id), need } : null;
  return {
    step,
    isDay,
    locked,
    weekNo: step ? weekOf(programme, step) : 0,
    dayLabel: step?.day_label ?? null,
    left,
    total,
    finished: !step,
    doneToday: locked ? dayDoneToday(programme, progress) : null,
    held,
  };
}

/** May this step be opened right now? Intros: yes (in order). Day steps: only the
 *  current one, and only if today's exercise isn't already done. */
export function canOpen(programme: Programme, step: Step, progress: ProgressState, devMode: boolean): boolean {
  if (progress.completedSteps.includes(step.id)) return true; // revisit
  if (devMode) return true;
  const next = nextStep(programme, progress);
  if (!next || next.id !== step.id) return false; // strictly in order
  if (isDayStep(step) && didDayToday(programme, progress)) {
    // Revisit of a held step already practised today is fine; any other day-step waits.
    return (progress.practiceDays?.[step.id] ?? []).includes(dayKey(new Date()));
  }
  return true;
}
